import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

/**
 * URLs and copy for trip share links. The public read-only itinerary lives on the web app at
 * `/share/:token` for every platform — recipients have no Elara account, so mobile owners share the
 * web URL too. Web passes its own origin so preview channels and local dev produce working links.
 */

/** Canonical production web origin, used by native clients that have no page origin of their own. */
export const WEB_APP_ORIGIN = 'https://app.elara.travel';

/**
 * Builds the public URL for a share token.
 *
 * @example
 * buildSharedItineraryUrl('abc'); // 'https://app.elara.travel/share/abc'
 * buildSharedItineraryUrl('abc', 'http://localhost:5173'); // 'http://localhost:5173/share/abc'
 */
export function buildSharedItineraryUrl(token: string, origin: string = WEB_APP_ORIGIN): string {
  return `${origin.replace(/\/+$/, '')}/share/${encodeURIComponent(token)}`;
}

/** One line of the share dialog's "What's shared" / "Never shared" summary. */
export interface ShareSummaryItem {
  icon: IconName;
  label: string;
  /** Optional clarifying line shown beneath the label. */
  detail?: string;
}

/**
 * The share dialog's description, shared by the web dialog and the mobile sheet so owners read the
 * same promise on every platform.
 */
export const SHARE_ITINERARY_DESCRIPTION =
  'Share a read-only version of your trip — handy for letting family or friends know where you’ll be.';

/** What a recipient sees — mirrors the server's allowlisted projection (`build-shared-itinerary.ts`). */
export const SHARED_ITINERARY_INCLUDES: readonly ShareSummaryItem[] = [
  { icon: 'calendar_month', label: 'Day-by-day schedule' },
  { icon: 'flight', label: 'Flights', detail: 'Airlines, flight numbers, and times' },
  { icon: 'hotel', label: 'Hotels', detail: 'Locations and contact details' },
  { icon: 'place', label: 'Places and activities', detail: 'Locations and contact details' },
];

/** What never leaves the account — the projection has no field for any of it. */
export const SHARED_ITINERARY_EXCLUDES: readonly ShareSummaryItem[] = [
  { icon: 'payments', label: 'Prices and costs' },
  { icon: 'person', label: 'Guest details', detail: 'Including names and contact details' },
  { icon: 'credit_card', label: 'Booking details', detail: 'Including confirmation numbers' },
  { icon: 'chat_bubble', label: 'Notes and chats', detail: 'Including attachments' },
];
